import { useEffect, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { useConnectivity } from '@/hooks/useConnectivity';

/**
 * Shows toast notifications for connectivity state changes.
 * Uses verified connectivity status instead of raw browser events.
 */
export const useNetworkStatus = () => {
  const { isOffline, isOnline, lastCheckedAt } = useConnectivity();
  const previousStatusRef = useRef<'online' | 'offline' | null>(null);
  const lastToastTimeRef = useRef<number>(0);
  const initialCheckCompletedRef = useRef(false);
  
  // Throttle: minimum 30 seconds between similar toasts
  const TOAST_THROTTLE_MS = 30000;

  useEffect(() => {
    // Wait for the initial connectivity check to complete before showing any toasts
    // This prevents false "offline" messages during page load
    if (!lastCheckedAt) {
      return;
    }
    
    // Mark initial check as completed after first successful check
    if (!initialCheckCompletedRef.current) {
      initialCheckCompletedRef.current = true;
      previousStatusRef.current = isOffline ? 'offline' : 'online';
      return; // Don't show toast on initial check completion
    }
    
    const currentStatus = isOffline ? 'offline' : 'online';
    const now = Date.now();
    
    // Skip if status hasn't changed
    if (previousStatusRef.current === currentStatus) {
      return;
    }
    
    if (previousStatusRef.current === currentStatus) {
      return;
    }

    // Check throttle
    if (now - lastToastTimeRef.current < TOAST_THROTTLE_MS) {
      previousStatusRef.current = currentStatus;
      return;
    }

    // Status changed - show appropriate toast
    if (currentStatus === 'offline' && previousStatusRef.current === 'online') {
      toast({
        title: "Connection lost",
        description: "Please check your internet connection. Some features may be unavailable.",
        variant: "destructive",
        duration: 5000,
      });
      lastToastTimeRef.current = now;
    } else if (currentStatus === 'online' && previousStatusRef.current === 'offline') {
      toast({
        title: "You're back online",
        description: "Your internet connection has been restored.",
        duration: 3000,
      });
      lastToastTimeRef.current = now;
    }

    previousStatusRef.current = currentStatus;
  }, [isOffline, isOnline, lastCheckedAt]);
};
